import {
  ENTRY_ORIGIN_LABEL,
  INVOICE_MODELS,
  INVOICE_MODEL_LABEL,
  PAYABLE_STATUS_LABEL,
  buildInstallments,
  formatMoney,
  itemMappingSchema,
  lineValue,
  manualItemsSchema,
  multiplyDecimal,
  purchaseEntryUpdateSchema,
  sumMoney,
  toFormMoney,
  type PayableStatus,
  type PurchaseEntry,
  type PurchaseEntryItem,
} from "@cafegestor/shared";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useCan } from "@/app/me";
import { Alert, Badge, Button, Field, Input, Select, Spinner, Table } from "@/components/ui";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { KeyFacts, NumberedSection, ProgressSteps } from "@/components/patterns";
import { apiDownload, errorMessage, fileToBase64 } from "@/lib/api";
import { toDecimal } from "@/lib/decimal";
import { Checkbox, SaveError } from "../cadastros/widgets";
import {
  dateBR,
  dateTimeBR,
  qty,
  unitCost,
  unitsOf,
  useDocAction,
  useProductDetail,
} from "../estoque/lib";
import { DocStatusBadge, ProductPicker, ReverseAction, WarehouseSelect } from "../estoque/pickers";
import { MovementsTable } from "../estoque/SaldosPage";
import { ENTRY_KEY, bytes, formatAccessKey, toInputQty, useEntry, usePaymentMethods } from "./lib";

const INVALIDATE = [ENTRY_KEY, ["estoque"], ["financeiro"], ["compras", "vinculos"]];

/**
 * Seções editáveis do rascunho. Uma seção sem alterações acompanha a versão atual da
 * entrada (recarrega quando ela muda). Com alterações não salvas, ela fica "presa" à
 * versão em que começou a edição: nada do que foi digitado some, e ao salvar a API
 * recusa (conflito de edição) se a entrada tiver mudado nesse meio-tempo. Enquanto uma
 * seção tem alterações, as outras não salvam.
 */
type Section = "header" | "items";
const SECTION_LABEL: Record<Section, string> = {
  header: "dados e parcelas",
  items: "itens",
};
const toInputMoney = (v: string | null | undefined) => toFormMoney(v);
const isValidNumber = (v: string) => /^\d+([.,]\d+)?$/.test(v.trim());

export function EntryEditor({ id, onClose }: { id: string; onClose: () => void }) {
  const entry = useEntry(id);
  if (entry.error) return <Alert tone="error">{errorMessage(entry.error)}</Alert>;
  if (!entry.data) return <Spinner />;
  return <EntryDetail key={entry.data.id} entry={entry.data} onClose={onClose} />;
}

function EntryDetail({ entry: e, onClose }: { entry: PurchaseEntry; onClose: () => void }) {
  const can = useCan();
  const draft = e.status === "rascunho";
  const canEdit = draft && can("compras.entrada.lancar");
  /** versão em que cada seção começou a ser editada (null = sem alterações) */
  const [pinned, setPinned] = useState<Record<Section, number | null>>({
    header: null,
    items: null,
  });
  const [resets, setResets] = useState<Record<Section, number>>({ header: 0, items: 0 });
  const dirty = pinned.header !== null || pinned.items !== null;
  const markDirty = (section: Section) => (d: boolean) =>
    setPinned((cur) => {
      const next = d ? (cur[section] ?? e.version) : null;
      return cur[section] === next ? cur : { ...cur, [section]: next };
    });
  const discard = (section: Section) => () => {
    setResets((r) => ({ ...r, [section]: r[section] + 1 }));
    markDirty(section)(false);
  };
  const sectionKey = (section: Section) =>
    `${section}-${resets[section]}-${pinned[section] ?? e.version}`;
  const sectionProps = (section: Section) => ({
    baseVersion: pinned[section] ?? e.version,
    onDirty: markDirty(section),
    onDiscard: discard(section),
    blockedBy: blockedBy(section),
  });
  /** outra seção com alterações não salvas (null = pode salvar esta) */
  function blockedBy(section: Section | null): string | null {
    const other = (Object.keys(pinned) as Section[]).find(
      (k) => k !== section && pinned[k] !== null,
    );
    return other ? SECTION_LABEL[other] : null;
  }
  const confirm = useDocAction<{ version: number }, PurchaseEntry>({
    path: () => `/compras/entradas/${e.id}/confirmar`,
    invalidate: INVALIDATE,
  });
  const cancel = useDocAction<Record<string, never>, PurchaseEntry>({
    path: () => `/compras/entradas/${e.id}/cancelar`,
    invalidate: INVALIDATE,
  });
  const reverse = useDocAction<{ reason: string }, PurchaseEntry>({
    path: () => `/compras/entradas/${e.id}/estornar`,
    invalidate: INVALIDATE,
  });
  const issues = e.issues ?? [];
  const [askCancel, setAskCancel] = useState(false);

  return (
    <section className="rounded-xl border bg-background/40 shadow-card">
      <header className="flex flex-wrap items-center justify-between gap-3 rounded-t-xl border-b border-border/70 bg-card px-5 py-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-semibold">
            Entrada {e.number} — {e.supplierName}
          </h2>
          <DocStatusBadge status={e.status} />
          <Badge>{ENTRY_ORIGIN_LABEL[e.origin]}</Badge>
        </div>
        <Button size="sm" variant="secondary" onClick={onClose}>
          Fechar
        </Button>
      </header>
      <div className="space-y-5 p-5 text-sm">
        <ProgressSteps
          label="Andamento da entrada"
          steps={["Lançamento", "Conferência sem pendências", "Confirmada"]}
          current={
            e.status === "confirmado"
              ? 3
              : e.status === "rascunho"
                ? issues.length > 0
                  ? 0
                  : 1
                : -1
          }
          {...(e.status === "estornado"
            ? { note: "Entrada estornada." }
            : e.status === "cancelado"
              ? { note: "Rascunho cancelado." }
              : {})}
        />
        <KeyFacts
          items={[
            { label: "Data da entrada", value: dateBR(e.entryDate) },
            { label: "Emissão", value: dateBR(e.issueDate) },
            { label: "Depósito", value: e.warehouseName },
            ...(e.invoiceNumber
              ? [
                  {
                    label: "Documento",
                    value: `${e.invoiceModel ? INVOICE_MODEL_LABEL[e.invoiceModel] : "Nota"} nº ${e.invoiceNumber}${e.invoiceSeries ? ` série ${e.invoiceSeries}` : ""}`,
                  },
                ]
              : []),
          ]}
        />
        {e.accessKey && (
          <p className="font-mono text-xs text-muted-foreground">
            Chave: {formatAccessKey(e.accessKey)}
          </p>
        )}
        {draft && issues.length > 0 && (
          <Alert tone="warn">
            <p className="font-medium">Para confirmar, falta:</p>
            <ul className="mt-1 list-disc pl-5">
              {issues.map((i) => (
                <li key={i}>{i}</li>
              ))}
            </ul>
          </Alert>
        )}
        {draft && (e.warnings?.length ?? 0) > 0 && (
          <Alert tone="info">
            <ul className="list-disc pl-5">
              {e.warnings!.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </Alert>
        )}

        <NumberedSection
          n={1}
          title="Fornecedor, documento e financeiro"
          description="Quem vendeu, a nota, o depósito e como será pago."
        >
          {canEdit ? (
            <HeaderForm key={sectionKey("header")} e={e} {...sectionProps("header")} />
          ) : (
            <InstallmentsView e={e} />
          )}
        </NumberedSection>

        <NumberedSection
          n={2}
          title="Produtos e vinculações"
          description={
            e.origin === "manual"
              ? "Itens lançados à mão, com lote e validade."
              : "Itens do XML ligados aos produtos do CaféGestor (de/para), com conversão de unidade."
          }
        >
          {canEdit && e.origin === "manual" ? (
            <ManualItems key={sectionKey("items")} e={e} {...sectionProps("items")} />
          ) : (
            <XmlItems e={e} canEdit={canEdit} blockedBy={blockedBy(null)} />
          )}
        </NumberedSection>

        <NumberedSection n={3} title="Tributos e valores">
          <Totals e={e} />
        </NumberedSection>

        {draft && can("compras.entrada.confirmar") && (
          <NumberedSection n={4} title="Conferência e confirmação">
            <p className="mb-3 text-muted-foreground">
              Lança a mercadoria no estoque (com lote e custo) e
              {e.generatesPayable
                ? " cria o título em Contas a pagar com as parcelas acima"
                : " não gera contas a pagar"}
              . Tudo de uma vez; não dá para desfazer, só estornar.
            </p>
            {dirty && <Alert tone="warn">Salve as alterações antes de confirmar.</Alert>}
            <div className="flex flex-wrap gap-2">
              <Button
                disabled={dirty || issues.length > 0 || confirm.isPending}
                onClick={() => confirm.mutate({ version: e.version })}
              >
                {confirm.isPending ? "Confirmando..." : "Confirmar entrada"}
              </Button>
              {can("compras.entrada.lancar") && (
                <Button
                  variant="secondary"
                  onClick={() => setAskCancel(true)}
                  disabled={cancel.isPending}
                >
                  Cancelar rascunho
                </Button>
              )}
              <ConfirmDialog
                open={askCancel}
                title={`Cancelar o rascunho da entrada ${e.number}?`}
                confirmLabel="Cancelar rascunho"
                cancelLabel="Voltar"
                destructive
                busy={cancel.isPending}
                onCancel={() => setAskCancel(false)}
                onConfirm={() => {
                  setAskCancel(false);
                  cancel.mutate({});
                }}
              >
                A entrada fica cancelada e não poderá ser confirmada. Nada entrou no estoque nem em
                Contas a pagar.
              </ConfirmDialog>
            </div>
            <div className="mt-2">
              <SaveError error={confirm.error ?? cancel.error} />
            </div>
          </NumberedSection>
        )}

        {e.payableNumber && (
          <Alert tone={e.payableStatus === "cancelado" ? "warn" : "ok"}>
            Título a pagar <span className="font-mono">{e.payableNumber}</span> —{" "}
            {PAYABLE_STATUS_LABEL[(e.payableStatus ?? "aberto") as PayableStatus]}.{" "}
            {can("financeiro.ver") && (
              <Link to="/financeiro/contas-a-pagar" className="underline">
                Ver em Contas a pagar
              </Link>
            )}
          </Alert>
        )}
        {e.status === "estornado" && (
          <Alert tone="warn">Estornada. Motivo: {e.reversalReason}</Alert>
        )}

        <Files e={e} />

        {(e.movements?.length ?? 0) > 0 && (
          <div>
            <h3 className="mb-2 font-semibold">Movimentos de estoque</h3>
            <MovementsTable rows={e.movements ?? []} />
          </div>
        )}
        {e.status === "confirmado" && can("compras.entrada.estornar") && (
          <ReverseAction
            label="Estornar entrada"
            pending={reverse.isPending}
            error={reverse.error}
            onConfirm={(reason) => reverse.mutate({ reason })}
          />
        )}
      </div>
    </section>
  );
}

function Totals({ e }: { e: PurchaseEntry }) {
  const rows: [string, string][] = [
    ["Produtos", e.productsTotal],
    ["Descontos", e.discountTotal],
    ["Frete", e.freightTotal],
    ["Seguro", e.insuranceTotal],
    ["Outras", e.otherTotal],
    ["IPI", e.ipiTotal],
    ["ICMS-ST", e.icmsStTotal],
  ];
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-1 rounded-md bg-muted px-4 py-3">
      {rows
        .filter(([, v]) => Number(v) !== 0)
        .map(([l, v]) => (
          <span key={l}>
            {l}: {formatMoney(v)}
          </span>
        ))}
      <span className="font-semibold">Total (custo): {formatMoney(e.totalAmount)}</span>
      {e.invoiceTotal !== null && <span>Total da nota: {formatMoney(e.invoiceTotal)}</span>}
      {e.generatesPayable && (
        <span className="font-semibold">A pagar: {formatMoney(e.payableAmount)}</span>
      )}
    </div>
  );
}

type InstallmentDraft = { key: number; dueDate: string; amount: string; document: string };

/** Aviso e botão "desfazer" das seções editáveis. */
function SectionGuard({
  changed,
  blockedBy,
  onDiscard,
}: {
  changed: boolean;
  blockedBy: string | null;
  onDiscard: () => void;
}) {
  return (
    <>
      {changed && (
        <Button variant="ghost" onClick={onDiscard}>
          Desfazer alterações
        </Button>
      )}
      {blockedBy && (
        <span className="text-muted-foreground">
          Salve ou desfaça antes as alterações em {blockedBy}.
        </span>
      )}
    </>
  );
}

type SectionProps = {
  e: PurchaseEntry;
  /** versão da entrada em que a edição começou (vai no salvar) */
  baseVersion: number;
  onDirty: (d: boolean) => void;
  onDiscard: () => void;
  blockedBy: string | null;
};

/** Dados do rascunho e parcelas a pagar. */
function HeaderForm({ e, baseVersion, onDirty, onDiscard, blockedBy }: SectionProps) {
  const methods = usePaymentMethods();
  const xml = e.origin === "xml";
  const [warehouseId, setWarehouseId] = useState(e.warehouseId);
  const [entryDate, setEntryDate] = useState(e.entryDate);
  const [issueDate, setIssueDate] = useState(e.issueDate ?? "");
  const [invoiceModel, setInvoiceModel] = useState<string>(e.invoiceModel ?? "55");
  const [invoiceSeries, setInvoiceSeries] = useState(e.invoiceSeries ?? "");
  const [invoiceNumber, setInvoiceNumber] = useState(e.invoiceNumber ?? "");
  const [paymentMethodId, setPaymentMethodId] = useState(e.paymentMethodId ?? "");
  const [generatesPayable, setGeneratesPayable] = useState(e.generatesPayable);
  const [notes, setNotes] = useState(e.notes ?? "");
  const [invoiceTotal, setInvoiceTotal] = useState(toInputMoney(e.invoiceTotal));
  const [rows, setRows] = useState<InstallmentDraft[]>(
    (e.installments ?? []).map((p, i) => ({
      key: i + 1,
      dueDate: p.dueDate,
      amount: toInputMoney(p.amount),
      document: p.document ?? "",
    })),
  );
  const [count, setCount] = useState(String(Math.max(1, e.installments?.length ?? 1)));
  const [formError, setFormError] = useState<string | null>(null);
  const [changed, setChanged] = useState(false);
  const touch = () => {
    setChanged(true);
    onDirty(true);
  };
  const save = useDocAction<unknown, PurchaseEntry>({
    path: () => `/compras/entradas/${e.id}`,
    method: "PUT",
    invalidate: INVALIDATE,
  });
  const method = methods.data?.items.find((m) => m.id === paymentMethodId);
  const sum = rows.every((r) => isValidNumber(r.amount))
    ? sumMoney(rows.map((r) => toDecimal(r.amount, "dinheiro")))
    : null;
  // valor a pagar: total da nota (se informado) ou o custo somado dos itens
  const payable = xml
    ? e.payableAmount
    : invoiceTotal.trim() === ""
      ? e.totalAmount
      : isValidNumber(invoiceTotal)
        ? toDecimal(invoiceTotal, "dinheiro")
        : null;

  function generate() {
    const baseDate = issueDate || entryDate;
    if (!baseDate) return setFormError("Informe a data da entrada para gerar as parcelas.");
    if (payable === null) return setFormError("Valor total da nota inválido.");
    const n = Math.min(120, Math.max(1, Number.parseInt(count, 10) || 1));
    let drafts: ReturnType<typeof buildInstallments>;
    try {
      drafts = buildInstallments({
        total: payable,
        count: n,
        baseDate,
        firstDueDays: method?.firstDueDays ?? 30,
        intervalDays: method?.intervalDays ?? 30,
      });
    } catch {
      return setFormError("Não foi possível gerar as parcelas: confira as datas e o valor.");
    }
    setFormError(null);
    setRows(
      drafts.map((d) => ({
        key: d.number,
        dueDate: d.dueDate,
        amount: toInputMoney(d.amount),
        document: "",
      })),
    );
    touch();
  }

  function submit() {
    const parsed = purchaseEntryUpdateSchema.safeParse({
      supplierId: e.supplierId,
      warehouseId,
      entryDate,
      issueDate,
      invoiceModel: xml ? e.invoiceModel : invoiceNumber ? invoiceModel : "",
      invoiceSeries: xml ? (e.invoiceSeries ?? "") : invoiceSeries,
      invoiceNumber: xml ? (e.invoiceNumber ?? "") : invoiceNumber,
      invoiceTotal: xml ? null : invoiceTotal,
      paymentMethodId,
      generatesPayable,
      notes,
      installments: generatesPayable
        ? rows.map((r) => ({ dueDate: r.dueDate, amount: r.amount, document: r.document }))
        : [],
      version: baseVersion,
    });
    if (!parsed.success) return setFormError(parsed.error.issues[0]?.message ?? "Dados inválidos");
    setFormError(null);
    save.mutate(parsed.data, { onSuccess: () => onDirty(false) });
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-4">
        <Field label="Depósito de entrada">
          <WarehouseSelect
            value={warehouseId}
            onChange={(v) => {
              setWarehouseId(v);
              touch();
            }}
          />
        </Field>
        <Field label="Data da entrada">
          <Input
            type="date"
            value={entryDate}
            onChange={(ev) => {
              setEntryDate(ev.target.value);
              touch();
            }}
          />
        </Field>
        <Field label="Emissão" hint={xml ? "Do XML" : undefined}>
          <Input
            type="date"
            value={issueDate}
            disabled={xml}
            onChange={(ev) => {
              setIssueDate(ev.target.value);
              touch();
            }}
          />
        </Field>
        <Field label="Forma de pagamento">
          <Select
            value={paymentMethodId}
            onChange={(ev) => {
              setPaymentMethodId(ev.target.value);
              touch();
            }}
          >
            <option value="">—</option>
            {methods.data?.items.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </Select>
        </Field>
        {!xml && (
          <>
            <Field
              label="Valor total da nota (R$)"
              hint="Vazio = custo dos itens. Informe se a nota tiver outro valor a pagar (ex.: ICMS desonerado)."
            >
              <Input
                inputMode="decimal"
                value={invoiceTotal}
                onChange={(ev) => {
                  setInvoiceTotal(ev.target.value);
                  touch();
                }}
              />
            </Field>
            <Field label="Documento">
              <Select
                value={invoiceModel}
                onChange={(ev) => {
                  setInvoiceModel(ev.target.value);
                  touch();
                }}
              >
                {INVOICE_MODELS.map((m) => (
                  <option key={m} value={m}>
                    {INVOICE_MODEL_LABEL[m]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Número da nota">
              <Input
                value={invoiceNumber}
                onChange={(ev) => {
                  setInvoiceNumber(ev.target.value);
                  touch();
                }}
              />
            </Field>
            <Field label="Série">
              <Input
                value={invoiceSeries}
                onChange={(ev) => {
                  setInvoiceSeries(ev.target.value);
                  touch();
                }}
              />
            </Field>
          </>
        )}
        <div className={xml ? "sm:col-span-4" : ""}>
          <Field label="Observações">
            <Input
              value={notes}
              onChange={(ev) => {
                setNotes(ev.target.value);
                touch();
              }}
            />
          </Field>
        </div>
      </div>

      <div className="rounded-md border p-3">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <Checkbox
            label="Gera contas a pagar"
            checked={generatesPayable}
            onChange={(ev) => {
              setGeneratesPayable(ev.target.checked);
              touch();
            }}
          />
          {generatesPayable && (
            <div className="flex items-end gap-2">
              <Field label="Parcelas">
                <Input
                  className="w-20"
                  inputMode="numeric"
                  value={count}
                  onChange={(ev) => setCount(ev.target.value)}
                />
              </Field>
              <Button size="sm" variant="secondary" onClick={generate}>
                Gerar{" "}
                {method
                  ? `(${method.firstDueDays}/${method.intervalDays} dias)`
                  : "(30 em 30 dias)"}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setRows((rs) => [
                    ...rs,
                    {
                      key: Math.max(0, ...rs.map((r) => r.key)) + 1,
                      dueDate: entryDate,
                      amount: "",
                      document: "",
                    },
                  ]);
                  touch();
                }}
              >
                Adicionar parcela
              </Button>
            </div>
          )}
        </div>
        {generatesPayable && (
          <>
            <div className="space-y-2">
              {rows.map((r, i) => (
                <div key={r.key} className="grid gap-2 sm:grid-cols-12 sm:items-end">
                  <span className="text-muted-foreground sm:col-span-1">{i + 1}ª</span>
                  <div className="sm:col-span-3">
                    <Input
                      type="date"
                      aria-label="Vencimento"
                      value={r.dueDate}
                      onChange={(ev) => {
                        setRows((rs) =>
                          rs.map((x) => (x.key === r.key ? { ...x, dueDate: ev.target.value } : x)),
                        );
                        touch();
                      }}
                    />
                  </div>
                  <div className="sm:col-span-3">
                    <Input
                      inputMode="decimal"
                      aria-label="Valor"
                      placeholder="Valor (R$)"
                      value={r.amount}
                      onChange={(ev) => {
                        setRows((rs) =>
                          rs.map((x) => (x.key === r.key ? { ...x, amount: ev.target.value } : x)),
                        );
                        touch();
                      }}
                    />
                  </div>
                  <div className="sm:col-span-3">
                    <Input
                      aria-label="Documento"
                      placeholder="Duplicata/boleto"
                      value={r.document}
                      onChange={(ev) => {
                        setRows((rs) =>
                          rs.map((x) =>
                            x.key === r.key ? { ...x, document: ev.target.value } : x,
                          ),
                        );
                        touch();
                      }}
                    />
                  </div>
                  <div className="text-right sm:col-span-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setRows((rs) => rs.filter((x) => x.key !== r.key));
                        touch();
                      }}
                    >
                      Remover
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-2 text-muted-foreground">
              Soma das parcelas: <strong>{sum === null ? "—" : formatMoney(sum)}</strong> · valor a
              pagar: <strong>{payable === null ? "—" : formatMoney(payable)}</strong>
            </p>
          </>
        )}
      </div>
      <div className="flex items-center gap-3">
        <Button
          variant="secondary"
          onClick={submit}
          disabled={!changed || save.isPending || blockedBy !== null}
        >
          Salvar dados e parcelas
        </Button>
        <SectionGuard changed={changed} blockedBy={blockedBy} onDiscard={onDiscard} />
        {formError && <span className="text-destructive">{formError}</span>}
      </div>
      <SaveError error={save.error} />
    </div>
  );
}

function InstallmentsView({ e }: { e: PurchaseEntry }) {
  if (!e.generatesPayable)
    return <p className="text-muted-foreground">Esta entrada não gera contas a pagar.</p>;
  return (
    <div>
      <h3 className="mb-2 font-semibold">Parcelas a pagar</h3>
      <Table head={["Parcela", "Vencimento", "Valor", "Documento"]}>
        {e.installments?.map((p) => (
          <tr key={p.id}>
            <td className="px-3 py-2">{p.number}</td>
            <td className="px-3 py-2">{dateBR(p.dueDate)}</td>
            <td className="px-3 py-2 text-right">{formatMoney(p.amount)}</td>
            <td className="px-3 py-2">{p.document ?? "—"}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

// ---------------------------------------------------------------- itens da entrada manual
type ItemDraft = {
  key: number;
  productId: string;
  label: string;
  unitId: string;
  quantity: string;
  unitPrice: string;
  discount: string;
  ipi: string;
  icmsSt: string;
  lotCode: string;
  lotManufacturedOn: string;
  lotExpiresOn: string;
};

function ManualItems({ e, baseVersion, onDirty, onDiscard, blockedBy }: SectionProps) {
  const [items, setItems] = useState<ItemDraft[]>(
    (e.items ?? []).map((i, n) => ({
      key: n + 1,
      productId: i.productId ?? "",
      label: i.productCode ? `${i.productCode} — ${i.productName}` : "",
      unitId: i.unitId ?? "",
      quantity: toInputQty(i.quantity),
      unitPrice: toInputQty(i.supplierUnitPrice),
      discount: toInputMoney(i.discount === "0.00" ? "" : i.discount),
      ipi: toInputMoney(i.ipi === "0.00" ? "" : i.ipi),
      icmsSt: toInputMoney(i.icmsSt === "0.00" ? "" : i.icmsSt),
      lotCode: i.lotCode ?? "",
      lotManufacturedOn: i.lotManufacturedOn ?? "",
      lotExpiresOn: i.lotExpiresOn ?? "",
    })),
  );
  const [freight, setFreight] = useState(
    toInputMoney(e.freightTotal === "0.00" ? "" : e.freightTotal),
  );
  const [insurance, setInsurance] = useState(
    toInputMoney(e.insuranceTotal === "0.00" ? "" : e.insuranceTotal),
  );
  const [other, setOther] = useState(toInputMoney(e.otherTotal === "0.00" ? "" : e.otherTotal));
  const [changed, setChanged] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const touch = () => {
    setChanged(true);
    onDirty(true);
  };
  const save = useDocAction<unknown, PurchaseEntry>({
    path: () => `/compras/entradas/${e.id}/itens`,
    method: "PUT",
    invalidate: INVALIDATE,
  });

  function submit() {
    const parsed = manualItemsSchema.safeParse({
      freight,
      insurance,
      other,
      version: baseVersion,
      items: items.map((i) => ({
        productId: i.productId,
        unitId: i.unitId,
        quantity: i.quantity,
        unitPrice: i.unitPrice,
        discount: i.discount,
        ipi: i.ipi,
        icmsSt: i.icmsSt,
        lotCode: i.lotCode,
        lotManufacturedOn: i.lotManufacturedOn,
        lotExpiresOn: i.lotExpiresOn,
      })),
    });
    if (!parsed.success) return setFormError(parsed.error.issues[0]?.message ?? "Dados inválidos");
    setFormError(null);
    save.mutate(parsed.data, { onSuccess: () => onDirty(false) });
  }

  return (
    <div className="space-y-3">
      {items.map((it) => (
        <ManualItemLine
          key={it.key}
          item={it}
          onChange={(v) => {
            touch();
            setItems((xs) => xs.map((x) => (x.key === v.key ? v : x)));
          }}
          onRemove={() => {
            touch();
            setItems((xs) => xs.filter((x) => x.key !== it.key));
          }}
        />
      ))}
      <div className="flex flex-wrap items-end gap-3">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            touch();
            setItems((xs) => [
              ...xs,
              {
                key: Math.max(0, ...xs.map((x) => x.key)) + 1,
                productId: "",
                label: "",
                unitId: "",
                quantity: "",
                unitPrice: "",
                discount: "",
                ipi: "",
                icmsSt: "",
                lotCode: "",
                lotManufacturedOn: "",
                lotExpiresOn: "",
              },
            ]);
          }}
        >
          Adicionar item
        </Button>
        <Field label="Frete (R$)" hint="Rateado pelo valor dos itens">
          <Input
            className="w-28"
            inputMode="decimal"
            value={freight}
            onChange={(ev) => {
              setFreight(ev.target.value);
              touch();
            }}
          />
        </Field>
        <Field label="Seguro (R$)">
          <Input
            className="w-28"
            inputMode="decimal"
            value={insurance}
            onChange={(ev) => {
              setInsurance(ev.target.value);
              touch();
            }}
          />
        </Field>
        <Field label="Outras despesas (R$)">
          <Input
            className="w-28"
            inputMode="decimal"
            value={other}
            onChange={(ev) => {
              setOther(ev.target.value);
              touch();
            }}
          />
        </Field>
        <Button onClick={submit} disabled={!changed || save.isPending || blockedBy !== null}>
          Salvar itens
        </Button>
        <SectionGuard changed={changed} blockedBy={blockedBy} onDiscard={onDiscard} />
      </div>
      {formError && <Alert tone="error">{formError}</Alert>}
      <SaveError error={save.error} />
      {(e.items?.length ?? 0) > 0 && !changed && <ItemsTable e={e} />}
    </div>
  );
}

function ManualItemLine({
  item,
  onChange,
  onRemove,
}: {
  item: ItemDraft;
  onChange: (v: ItemDraft) => void;
  onRemove: () => void;
}) {
  const product = useProductDetail(item.productId);
  const units = unitsOf(product.data);
  const firstUnit = units[0]?.id;
  useEffect(() => {
    if (!item.unitId && firstUnit) onChange({ ...item, unitId: firstUnit });
  }, [item, firstUnit, onChange]);
  const value =
    isValidNumber(item.quantity) && isValidNumber(item.unitPrice)
      ? lineValue(toDecimal(item.quantity), toDecimal(item.unitPrice))
      : null;
  return (
    <div className="grid gap-2 rounded-md border p-2 sm:grid-cols-12 sm:items-end">
      <div className="sm:col-span-4">
        <Field label="Produto">
          <ProductPicker
            value={item.productId}
            label={item.label}
            onChange={(id, l) => onChange({ ...item, productId: id, label: l, unitId: "" })}
          />
        </Field>
      </div>
      <div className="sm:col-span-1">
        <Field label="Un.">
          <Select
            value={item.unitId}
            onChange={(ev) => onChange({ ...item, unitId: ev.target.value })}
          >
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.code}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div className="sm:col-span-1">
        <Field label="Qtde">
          <Input
            inputMode="decimal"
            value={item.quantity}
            onChange={(ev) => onChange({ ...item, quantity: ev.target.value })}
          />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field label="Preço unit. (R$)" hint={value ? `= ${formatMoney(value)}` : undefined}>
          <Input
            inputMode="decimal"
            value={item.unitPrice}
            onChange={(ev) => onChange({ ...item, unitPrice: ev.target.value })}
          />
        </Field>
      </div>
      <div className="sm:col-span-1">
        <Field label="Desc.">
          <Input
            inputMode="decimal"
            value={item.discount}
            onChange={(ev) => onChange({ ...item, discount: ev.target.value })}
          />
        </Field>
      </div>
      <div className="sm:col-span-1">
        <Field label="IPI">
          <Input
            inputMode="decimal"
            value={item.ipi}
            onChange={(ev) => onChange({ ...item, ipi: ev.target.value })}
          />
        </Field>
      </div>
      <div className="sm:col-span-1">
        <Field label="ST">
          <Input
            inputMode="decimal"
            value={item.icmsSt}
            onChange={(ev) => onChange({ ...item, icmsSt: ev.target.value })}
          />
        </Field>
      </div>
      <div className="text-right sm:col-span-1">
        <Button size="sm" variant="ghost" onClick={onRemove}>
          Remover
        </Button>
      </div>
      {product.data?.controlsLot && (
        <>
          <div className="sm:col-span-4">
            <Field label="Lote">
              <Input
                value={item.lotCode}
                onChange={(ev) => onChange({ ...item, lotCode: ev.target.value })}
              />
            </Field>
          </div>
          <div className="sm:col-span-3">
            <Field label="Fabricação">
              <Input
                type="date"
                value={item.lotManufacturedOn}
                onChange={(ev) => onChange({ ...item, lotManufacturedOn: ev.target.value })}
              />
            </Field>
          </div>
          <div className="sm:col-span-3">
            <Field label="Validade">
              <Input
                type="date"
                value={item.lotExpiresOn}
                onChange={(ev) => onChange({ ...item, lotExpiresOn: ev.target.value })}
              />
            </Field>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- itens do XML (de/para)
function XmlItems({
  e,
  canEdit,
  blockedBy,
}: {
  e: PurchaseEntry;
  canEdit: boolean;
  blockedBy: string | null;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  if ((e.items?.length ?? 0) === 0) return <p className="text-muted-foreground">Nenhum item.</p>;
  if (!canEdit) return <ItemsTable e={e} />;
  return (
    <div className="space-y-2">
      {e.items!.map((i) => (
        <div key={i.id} className="rounded-md border p-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p>
                <span className="text-muted-foreground">#{i.lineNumber}</span>{" "}
                <span className="font-mono text-xs">{i.supplierCode}</span> {i.supplierDescription}
              </p>
              <p className="text-muted-foreground">
                Fornecedor: {qty(i.supplierQuantity, i.supplierUnit ?? "")} · custo{" "}
                {formatMoney(i.totalCost)}
                {i.supplierLot ? ` · lote ${i.supplierLot}` : ""}
                {i.ncm ? ` · NCM ${i.ncm}` : ""}
              </p>
              {i.productId ? (
                <p>
                  <Badge tone="ok">vinculado</Badge> {i.productCode} — {i.productName}:{" "}
                  <strong>{qty(i.quantity, i.unitCode ?? "")}</strong> (1 {i.supplierUnit} ={" "}
                  {qty(i.conversion)} {i.unitCode}){i.lotCode ? ` · lote ${i.lotCode}` : ""}
                </p>
              ) : (
                <Badge tone="warn">sem produto do CaféGestor</Badge>
              )}
            </div>
            <Button
              size="sm"
              variant={i.productId ? "ghost" : "primary"}
              onClick={() => setEditing(i.id)}
            >
              {i.productId ? "Alterar vínculo" : "Vincular produto"}
            </Button>
          </div>
          {editing === i.id && (
            <MappingForm e={e} item={i} blockedBy={blockedBy} onClose={() => setEditing(null)} />
          )}
        </div>
      ))}
    </div>
  );
}

function MappingForm({
  e,
  item,
  blockedBy,
  onClose,
}: {
  e: PurchaseEntry;
  item: PurchaseEntryItem;
  blockedBy: string | null;
  onClose: () => void;
}) {
  const [productId, setProductId] = useState(item.productId ?? "");
  const [label, setLabel] = useState(
    item.productCode ? `${item.productCode} — ${item.productName}` : "",
  );
  const [unitId, setUnitId] = useState(item.unitId ?? "");
  const [conversion, setConversion] = useState(
    toInputQty(item.conversion === "1" && !item.productId ? "" : item.conversion),
  );
  const [lotCode, setLotCode] = useState(item.lotCode ?? "");
  const [lotManufacturedOn, setLotManufacturedOn] = useState(item.lotManufacturedOn ?? "");
  const [lotExpiresOn, setLotExpiresOn] = useState(item.lotExpiresOn ?? "");
  const [remember, setRemember] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);
  const product = useProductDetail(productId);
  const units = unitsOf(product.data);
  const unit = units.find((u) => u.id === unitId) ?? units[0];
  const save = useDocAction<unknown, PurchaseEntry>({
    path: () => `/compras/entradas/${e.id}/itens/${item.id}/vinculo`,
    method: "PUT",
    invalidate: INVALIDATE,
    onDone: onClose,
  });
  const preview =
    item.supplierQuantity && isValidNumber(conversion)
      ? multiplyDecimal(item.supplierQuantity, toDecimal(conversion))
      : null;

  function submit() {
    const parsed = itemMappingSchema.safeParse({
      productId,
      unitId: unit?.id ?? "",
      conversion: conversion || (unit?.code === item.supplierUnit ? "1" : ""),
      lotCode: product.data?.controlsLot ? lotCode : "",
      lotManufacturedOn: product.data?.controlsLot ? lotManufacturedOn : "",
      lotExpiresOn: product.data?.controlsLot ? lotExpiresOn : "",
      remember,
      version: e.version,
    });
    if (!parsed.success) return setFormError(parsed.error.issues[0]?.message ?? "Dados inválidos");
    setFormError(null);
    save.mutate(parsed.data);
  }

  return (
    <div className="mt-3 space-y-3 rounded-md bg-muted p-3">
      <div className="grid gap-3 sm:grid-cols-6 sm:items-end">
        <div className="sm:col-span-3">
          <Field label="Produto do CaféGestor">
            <ProductPicker
              value={productId}
              label={label}
              onChange={(id, l) => {
                setProductId(id);
                setLabel(l);
                setUnitId("");
              }}
            />
          </Field>
        </div>
        <Field label="Unidade">
          <Select value={unit?.id ?? ""} onChange={(ev) => setUnitId(ev.target.value)}>
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.code}
              </option>
            ))}
          </Select>
        </Field>
        <div className="sm:col-span-2">
          <Field
            label={`1 ${item.supplierUnit ?? "un."} do fornecedor = ? ${unit?.code ?? ""}`}
            hint={preview ? `Entram ${qty(preview, unit?.code ?? "")}` : "Ex.: saca de 60 kg → 60"}
          >
            <Input
              inputMode="decimal"
              value={conversion}
              onChange={(ev) => setConversion(ev.target.value)}
            />
          </Field>
        </div>
        {product.data?.controlsLot && (
          <>
            <div className="sm:col-span-2">
              <Field
                label="Lote"
                hint={item.supplierLot ? `Do fornecedor: ${item.supplierLot}` : undefined}
              >
                <Input value={lotCode} onChange={(ev) => setLotCode(ev.target.value)} />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Fabricação">
                <Input
                  type="date"
                  value={lotManufacturedOn}
                  onChange={(ev) => setLotManufacturedOn(ev.target.value)}
                />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Validade">
                <Input
                  type="date"
                  value={lotExpiresOn}
                  onChange={(ev) => setLotExpiresOn(ev.target.value)}
                />
              </Field>
            </div>
          </>
        )}
      </div>
      <Checkbox
        label="Lembrar este vínculo para as próximas notas deste fornecedor (de/para)"
        checked={remember}
        onChange={(ev) => setRemember(ev.target.checked)}
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={submit}
          disabled={save.isPending || !productId || blockedBy !== null}
        >
          Salvar vínculo
        </Button>
        <Button size="sm" variant="secondary" onClick={onClose}>
          Fechar
        </Button>
        {blockedBy && (
          <span className="self-center text-muted-foreground">
            Salve ou desfaça antes as alterações em {blockedBy}.
          </span>
        )}
      </div>
      {formError && <Alert tone="error">{formError}</Alert>}
      <SaveError error={save.error} />
    </div>
  );
}

function ItemsTable({ e }: { e: PurchaseEntry }) {
  return (
    <Table head={["#", "Produto", "Quantidade", "Lote", "Custo do item", "Custo unit."]}>
      {e.items?.map((i) => (
        <tr key={i.id}>
          <td className="px-3 py-2">{i.lineNumber}</td>
          <td className="px-3 py-2">
            {i.productId ? (
              <>
                <span className="font-mono text-xs">{i.productCode}</span> {i.productName}
              </>
            ) : (
              <span className="text-muted-foreground">
                {i.supplierCode} {i.supplierDescription} (sem vínculo)
              </span>
            )}
          </td>
          <td className="px-3 py-2 text-right whitespace-nowrap">
            {qty(i.quantity, i.unitCode ?? "")}
          </td>
          <td className="px-3 py-2 font-mono">{i.lotCode ?? "—"}</td>
          <td className="px-3 py-2 text-right whitespace-nowrap">{formatMoney(i.totalCost)}</td>
          <td className="px-3 py-2 text-right whitespace-nowrap">
            {i.unitCost ? `${unitCost(i.unitCost)}/${i.baseUnitCode}` : "—"}
          </td>
        </tr>
      ))}
    </Table>
  );
}

// ---------------------------------------------------------------- documentos
// anexar não muda a versão da entrada: não interfere nas seções em edição
function Files({ e }: { e: PurchaseEntry }) {
  const can = useCan();
  const [file, setFile] = useState<File | null>(null);
  /** muda para limpar o campo de arquivo depois de anexar */
  const [inputKey, setInputKey] = useState(0);
  const [description, setDescription] = useState("");
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const upload = useDocAction<
    { fileName: string; contentBase64: string; description: string },
    PurchaseEntry
  >({
    path: () => `/compras/entradas/${e.id}/anexos`,
    invalidate: INVALIDATE,
    onDone: () => {
      setFile(null);
      setDescription("");
      setInputKey((k) => k + 1);
    },
  });
  const canAttach = can("compras.entrada.lancar") && e.status !== "cancelado";

  async function send() {
    if (!file) return;
    setDownloadError(null);
    if (file.size > 5 * 1024 * 1024) return setDownloadError("Arquivo maior que 5 MB.");
    let contentBase64: string;
    try {
      contentBase64 = await fileToBase64(file);
    } catch {
      return setDownloadError("Não foi possível ler o arquivo. Escolha-o de novo.");
    }
    upload.mutate({ fileName: file.name, contentBase64, description });
  }

  return (
    <div>
      <h3 className="mb-2 font-semibold">Documentos (armazenamento privado)</h3>
      {(e.files?.length ?? 0) === 0 ? (
        <p className="text-muted-foreground">Nenhum documento.</p>
      ) : (
        <Table head={["Arquivo", "Descrição", "Tamanho", "Incluído", ""]}>
          {e.files?.map((f) => (
            <tr key={f.id}>
              <td className="px-3 py-2">
                {f.fileName} {f.kind === "nfe_xml" && <Badge>XML original</Badge>}
              </td>
              <td className="px-3 py-2">{f.description ?? "—"}</td>
              <td className="px-3 py-2">{bytes(f.sizeBytes)}</td>
              <td className="px-3 py-2 text-xs">
                {dateTimeBR(f.createdAt)} · {f.createdByEmail ?? "—"}
              </td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setDownloadError(null);
                    apiDownload(`/compras/entradas/${e.id}/arquivos/${f.fileId}`, f.fileName).catch(
                      (err: unknown) => setDownloadError(errorMessage(err)),
                    );
                  }}
                >
                  Baixar
                </Button>
                {f.kind === "anexo" && can("compras.entrada.lancar") && (
                  <RemoveAttachment entryId={e.id} linkId={f.id} />
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}
      {canAttach && (
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <Field label="Anexar (PDF, PNG, JPEG ou XML até 5 MB)">
            <Input
              key={inputKey}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.xml,application/pdf,image/png,image/jpeg,application/xml,text/xml"
              onChange={(ev) => setFile(ev.target.files?.[0] ?? null)}
            />
          </Field>
          <Field label="Descrição">
            <Input
              value={description}
              onChange={(ev) => setDescription(ev.target.value)}
              placeholder="Ex.: boleto"
            />
          </Field>
          <Button
            variant="secondary"
            disabled={!file || upload.isPending}
            onClick={() => void send()}
          >
            Anexar
          </Button>
        </div>
      )}
      <div className="mt-2 space-y-2">
        {downloadError && <Alert tone="error">{downloadError}</Alert>}
        <SaveError error={upload.error} />
      </div>
    </div>
  );
}

function RemoveAttachment({ entryId, linkId }: { entryId: string; linkId: string }) {
  const remove = useDocAction<undefined, PurchaseEntry>({
    path: () => `/compras/entradas/${entryId}/anexos/${linkId}`,
    method: "DELETE",
    invalidate: INVALIDATE,
  });
  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        disabled={remove.isPending}
        onClick={() => remove.mutate(undefined)}
      >
        Retirar
      </Button>
      {remove.error !== null && (
        <span className="text-xs text-destructive">{errorMessage(remove.error)}</span>
      )}
    </>
  );
}
